<template>
  <layout-waiter :nav-menu-items="navMenuItems">
    <slot v-if="!isLoading" />

    <!-- Slot: Navbar -->
    <template #navbar="{ toggleVerticalNavMenuActive }">
      <div class="navbar-content-container">
        <!-- Left Content: -->
        <div class="d-flex align-center">
          <v-icon
            v-if="$vuetify.breakpoint.mdAndDown"
            class="me-3"
            @click="toggleVerticalNavMenuActive"
          >
            {{ icons.mdiMenu }}
          </v-icon>

          <v-btn
            v-if="['staging', 'development'].includes(currentEnv)"
            color="error"
            :ripple="false"
            small
          >
            {{
              {
                staging: 'STG',
                development: 'DEV'
              }[currentEnv]
            }}
          </v-btn>
        </div>

        <!-- Right Content: -->
        <div class="d-flex align-center right-row">
          <clock class="mr-4" />
          <v-btn
            icon
            depressed
            :ripple="false"
            :loading="isLoading"
            @click="$router.go(0)"
          >
            <v-icon>
              {{ icons.mdiReload }}
            </v-icon>
          </v-btn>
          <app-bar-theme-switcher class="mr-2" />
          <app-bar-notification
            :notifications="currentClubNotifications"
            :color="bellColor"
            class="mr-2"
            @update:close-notification-card="setColorEmpty"
          />
          <monitor-menu class="mr-5" />
          <cash-register-menu />
        </div>
      </div>
    </template>

    <!-- Slot: Footer -->
    <template #footer>
      <!-- TODO -->
    </template>
  </layout-waiter>
</template>

<script>
// App Bar Components
import AppBarThemeSwitcher from '@core/layouts/components/app-bar/AppBarThemeSwitcher.vue'
import AppBarNotification from '@core/layouts/components/app-bar/WaiterAppBarNotification.vue'
import MonitorMenu from '@core/layouts/components/app-bar/MonitorMenu.vue'

import { mdiMenu, mdiHeartOutline, mdiReload } from '@mdi/js'

import CashRegisterMenu from '@core/layouts/components/app-bar/CashRegisterMenu.vue'

import {
  reactive,
  provide,
  getCurrentInstance,
  ref,
} from '@vue/composition-api'

import ClubApi from '@/api/waiter/Club'
import ReportApi from '@/api/waiter/Report'
import ReceiptNotePresetApi from '@/api/v2/utility/settings/ReceiptNotePreset'
import CustomerTagApi from '@/api/waiter/CustomerTag'
import MonitorTimecardApi from '@/api/v2/monitor/Timecard'
import MonitorTableApi from '@/api/v2/monitor/Table'
import MonitorVendingHistoryApi from '@/api/v2/monitor/VendingHistory'

import navMenuItems from '@/navigation/waiter'
import LayoutWaiter from '@/@core/layouts/variants/LayoutWaiter.vue'

import Clock from '@/views/components/util/Clock.vue'
import useDateFormat from '@/views/composable/useDateFormat'
import { cable } from '@/plugins/websocket-client'

import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import useCompInit from '@/views/composable/useCompInit'

export default {
  components: {
    LayoutWaiter,

    // App Bar Components
    AppBarThemeSwitcher,
    AppBarNotification,

    Clock,
    CashRegisterMenu,
    MonitorMenu,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { isLoading, initWith } = useCompInit()
    const { dateTime } = useDateFormat()
    const currentClubMeta = reactive({
      remainingCashRegisterMoney: 0,
      todaySalesUnfixed: 0,
      todaySales: 0,
      today: new Date().toISOString().substr(0, 10),
    })
    const currentClubNotifications = ref([])
    const bellColor = ref('')
    const {
      isConnected,
      isConnecting,
      printer,
      checkConnection,
    } = useReceiptPrinterConnection()
    const receiptNotePresets = ref([])
    const customerTags = ref([])
    const currentStats = reactive({
      castCount: 0,
      tableCount: 0,
      customerCount: 0,
      castWorkingTime: 0,
      customerUsageTime: 0,
    })

    const getClub = async () => {
      const res = await ClubApi.getClub()

      if (res?.data?.data) {
        const {
          remainingCashRegisterMoney,
          todaySalesUnfixed,
          todaySales,
          today,
        } = res.data.data.meta

        currentClubMeta.remainingCashRegisterMoney = remainingCashRegisterMoney
        currentClubMeta.todaySalesUnfixed = todaySalesUnfixed
        currentClubMeta.todaySales = todaySales
        currentClubMeta.today = today
      }
    }

    const getReceiptNotePresets = async () => {
      try {
        const res = await ReceiptNotePresetApi.getReceiptNotePresets()
        receiptNotePresets.value = res.data.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }

    const getActiveCastCount = async () => {
      try {
        const res = await MonitorTimecardApi.getActiveCastCount()
        currentStats.castCount = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    const getActiveTableCounts = async () => {
      try {
        const res = await MonitorTableApi.getActiveTableCounts()
        const { tableCount, customerCount } = res.data.data
        currentStats.tableCount = tableCount
        currentStats.customerCount = customerCount
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    const fetchPlusMinusStats = async () => {
      await Promise.all([getActiveCastCount(), getActiveTableCounts()])
    }

    const getCastWorkingTime = async () => {
      try {
        const res = await MonitorTimecardApi.getCastWorkingTime()
        currentStats.castWorkingTime = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    const getCustomerUsageTime = async () => {
      try {
        const res = await MonitorVendingHistoryApi.getCustomerUsageTime()
        currentStats.customerUsageTime = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    // 稼働率の情報を取得
    const fetchOperationRateStats = async () => {
      await Promise.all([getCastWorkingTime(), getCustomerUsageTime()])
    }

    const getCustomerTags = async () => {
      const res = await CustomerTagApi.getCustomerTags()
      if (res?.data) {
        customerTags.value = [...res.data.customerTags.data]
      }
    }

    const getReport = async () => {
      const res = await ReportApi.getReports()
      if (res?.data?.data) {
        currentClubNotifications.value = res.data.data.map(report => ({ ...report.attributes.report, time: dateTime(report.attributes.report.time) }))
      }
    }

    const subscription = cable.subscriptions.create(
      {
        channel: 'NotificationChannel',
      },
      {
        connected: () => {
        },
        disconnected: () => {},
        received: ({
          title,
          time,
        }) => {
          bellColor.value = 'success'
          currentClubNotifications.value.unshift({ title, time: dateTime(time) })
        },
      },
    )

    const setColorEmpty = () => {
      bellColor.value = ''
    }

    checkConnection()
    initWith([
      getClub(),
      getReceiptNotePresets(),
      getReport(),
      getCustomerTags(),
      fetchPlusMinusStats(),
      fetchOperationRateStats(),
    ])

    provide('currentClubMeta', currentClubMeta)
    provide('getClub', getClub)
    provide('isReceiptPrinterConnected', isConnected)
    provide('isReceiptPrinterConnecting', isConnecting)
    provide('printer', printer)
    provide('noticeSubscription', subscription)
    provide('receiptNotePresets', receiptNotePresets)
    provide('customerTags', customerTags)
    provide('currentStats', currentStats)
    provide('fetchPlusMinusStats', fetchPlusMinusStats)
    provide('fetchOperationRateStats', fetchOperationRateStats)

    return {
      // data
      navMenuItems,
      isLoading,
      bellColor,
      currentClubNotifications,
      currentStats,

      // methods
      setColorEmpty,

      icons: {
        mdiMenu,
        mdiHeartOutline,
        mdiReload,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.navbar-content-container {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
  .expanded-search {
    ::v-deep .app-bar-autocomplete-box div[role='combobox'] {
      background-color: transparent;
    }

    > .d-flex > button.v-icon {
      display: none;
    }

    // ===

    & > .right-row {
      visibility: hidden;
      opacity: 0;
    }
  }
}
</style>
